<ng-container *ngIf="viewModel$ | async as vm">
  <p-dialog
    [visible]="vm.state.open"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [closable]="!vm.state.submitting && !vm.retrying"
    [closeOnEscape]="!vm.state.submitting && !vm.retrying"
    [style]="vm.state.finished
      ? { width: vm.documents.length > 1 ? '36rem' : '32rem', maxWidth: '95vw' }
      : { width: '88rem', maxWidth: '96vw', height: '92vh' }"
    [contentStyle]="{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }"
    styleClass="create-document-dialog"
    (visibleChange)="!$event && close()"
    (onShow)="hideChatWidgetOnMobile()"
    (onHide)="restoreChatWidget()"
  >
    <ng-template pTemplate="header">
      <span class="title">
        {{ vm.state.finished && vm.documents.length === 1 ? vm.documentsLabel : 'Create Document' }}
        <span *ngIf="vm.jobCode as code" class="job-code">{{ code }}</span>
      </span>
    </ng-template>

    <!-- Success screen: one document -->
    <ng-container *ngIf="vm.state.finished && vm.documents.length === 1">
      <div *ngIf="vm.documents[0] as doc" class="success">
        <ng-container *ngIf="doc.result?.sent; else notSent">
          <i class="pi pi-check-circle success-icon"></i>
          <h3>{{ doc.name }} sent</h3>
          <p>
            We sent a link to
            <b>{{ vm.customerName || 'the customer' }}</b>.
          </p>
        </ng-container>
        <ng-template #notSent>
          <ng-container *ngIf="!doc.result?.sendError; else sendFailed">
            <i class="pi pi-check-circle success-icon"></i>
            <h3>{{ doc.name }} created</h3>
            <p>It has not been sent. Download it to send it yourself.</p>
          </ng-container>
          <ng-template #sendFailed>
            <i class="pi pi-exclamation-triangle warning-icon"></i>
            <h3>{{ doc.name }} created, but not sent</h3>
            <p>{{ doc.result?.sendError }}</p>
            <button
              pButton
              class="p-button-outlined"
              icon="pi pi-send"
              label="Try sending again"
              [loading]="doc.resending"
              [disabled]="doc.resending"
              (click)="store.dispatch(actions.trySendingAgainButtonClicked({ docType: doc.docType }))"
            ></button>
          </ng-template>
        </ng-template>
        <div class="success-actions">
          <button
            pButton
            class="p-button-text"
            icon="pi pi-download"
            [label]="'Download ' + doc.name"
            (click)="store.dispatch(actions.downloadButtonClicked({ docType: doc.docType }))"
          ></button>
          <button pButton label="Close" [disabled]="vm.retrying" (click)="close()"></button>
        </div>
      </div>
    </ng-container>

    <!-- Success screen: several documents -->
    <div *ngIf="vm.state.finished && vm.documents.length > 1" class="success">
      <ng-container *ngIf="!vm.resultHasProblems; else someFailed">
        <i class="pi pi-check-circle success-icon"></i>
        <h3>{{ vm.documentsLabel }} {{ vm.allSent ? 'sent' : 'created' }}</h3>
      </ng-container>
      <ng-template #someFailed>
        <i class="pi pi-exclamation-triangle warning-icon"></i>
        <h3>Some documents need attention</h3>
      </ng-template>
      <ul class="result-list">
        <li *ngFor="let doc of vm.documents; trackBy: trackByDocType" class="result-row">
          <i
            class="pi result-icon"
            [class.pi-check-circle]="doc.result?.artifact && !doc.result?.sendError"
            [class.pi-exclamation-triangle]="doc.result?.sendError"
            [class.pi-times-circle]="doc.result?.createError"
          ></i>
          <div class="result-body">
            <div class="result-name">{{ doc.name }}</div>
            <small *ngIf="doc.result?.createError as error" class="result-error">Not created. {{ error }}</small>
            <small *ngIf="doc.result?.sendError as error" class="result-error">Created, but not sent. {{ error }}</small>
            <small *ngIf="doc.result?.sent">Sent to {{ vm.customerName || 'the customer' }}</small>
            <small *ngIf="doc.result?.artifact && !doc.result?.sent && !doc.result?.sendError">Created, not sent</small>
            <button
              *ngIf="doc.result?.createError"
              pButton
              class="p-button-outlined p-button-sm try-again"
              icon="pi pi-refresh"
              label="Try creating again"
              [loading]="doc.creating"
              [disabled]="doc.creating"
              (click)="store.dispatch(actions.tryCreatingAgainButtonClicked({ docType: doc.docType }))"
            ></button>
            <button
              *ngIf="doc.result?.sendError"
              pButton
              class="p-button-outlined p-button-sm try-again"
              icon="pi pi-send"
              label="Try sending again"
              [loading]="doc.resending"
              [disabled]="doc.resending"
              (click)="store.dispatch(actions.trySendingAgainButtonClicked({ docType: doc.docType }))"
            ></button>
          </div>
          <button
            *ngIf="doc.result?.artifact"
            pButton
            class="p-button-text p-button-rounded"
            icon="pi pi-download"
            [pTooltip]="'Download ' + doc.name"
            [attr.aria-label]="'Download ' + doc.name"
            (click)="store.dispatch(actions.downloadButtonClicked({ docType: doc.docType }))"
          ></button>
        </li>
      </ul>
      <div class="success-actions">
        <button pButton label="Close" [disabled]="vm.retrying" (click)="close()"></button>
      </div>
    </div>

    <!-- Create screen -->
    <div *ngIf="!vm.state.finished" class="create">
      <div class="settings">
        <ng-template #checklist>
          <div class="check-list" role="group" aria-labelledby="create-document-types">
            <div
              *ngFor="let row of vm.checklist; trackBy: trackByDocType"
              class="check-row"
              [class.ticked]="row.ticked"
              [class.previewing]="row.previewing"
              [attr.data-doc-type]="row.docType"
              (click)="!vm.state.submitting && store.dispatch(actions.documentRowClicked({ docType: row.docType }))"
            >
              <p-checkbox
                [binary]="true"
                [ngModel]="row.ticked"
                [disabled]="!!vm.state.submitting"
                (click)="$event.stopPropagation()"
                (onChange)="store.dispatch(actions.documentCheckboxClicked({ docType: row.docType }))"
              ></p-checkbox>
              <span class="name">{{ row.name }}</span>
              <button
                *ngIf="row.ticked"
                type="button"
                class="preview-button"
                [class.active]="row.previewing"
                [attr.aria-pressed]="row.previewing"
                [attr.aria-label]="'Preview ' + row.name + (row.needsAttention ? ' (needs attention)' : '')"
                [pTooltip]="row.needsAttention ? 'Needs attention' : undefined"
                [disabled]="!!vm.state.submitting"
                (click)="$event.stopPropagation(); store.dispatch(actions.previewButtonClicked({ docType: row.docType })); documentPicker.hide()"
              >
                <i *ngIf="row.needsAttention" class="pi pi-exclamation-triangle attention-icon"></i>
                <span>{{ row.previewing ? 'Previewing' : 'Preview' }}</span>
                <i class="pi pi-eye"></i>
              </button>
            </div>
          </div>
        </ng-template>

        <div class="field">
          <label id="create-document-types">Documents</label>
          <div class="checklist-inline">
            <ng-container *ngTemplateOutlet="checklist"></ng-container>
          </div>
          <button
            type="button"
            class="document-picker"
            aria-haspopup="true"
            [attr.aria-expanded]="documentPicker.overlayVisible"
            [disabled]="!!vm.state.submitting"
            (click)="documentPicker.toggle($event)"
          >
            <span class="name">{{ vm.previewed?.name ?? 'Select a document' }}</span>
            <small *ngIf="vm.documents.length > 1">{{ vm.previewed?.position }} of {{ vm.documents.length }}</small>
            <i *ngIf="vm.needsAttention.length" class="pi pi-exclamation-triangle attention-icon"></i>
            <i class="pi pi-chevron-down"></i>
          </button>
          <p-overlayPanel #documentPicker appendTo="body" styleClass="create-document-picker">
            <ng-template pTemplate="content">
              <ng-container *ngTemplateOutlet="checklist"></ng-container>
            </ng-template>
          </p-overlayPanel>
          <small class="hint">
            Looking to create an invoice?
            <span class="freya-link-button" (click)="store.dispatch(actions.createInvoiceLinkClicked())">Click here</span>
          </small>
        </div>

        <ng-container *ngIf="vm.previewed as doc">
          <h3 class="options-heading">{{ doc.name }} options</h3>

          <ng-container *ngIf="doc.preview && !doc.previewLoading">
            <div *ngFor="let control of vm.controls; trackBy: trackByName" class="field">
              <ng-container [ngSwitch]="control.kind">

                <ng-container *ngSwitchCase="'events'">
                  <ng-container *ngIf="control.kind === 'events'">
                    <div class="events-header">
                      <p-checkbox
                        [binary]="true"
                        [ngModel]="control.allSelected"
                        [disabled]="control.allSelected || !!vm.state.submitting"
                        [label]="control.option.label"
                        (onChange)="selectAllEvents(control)"
                      ></p-checkbox>
                      <b>{{ control.selectedTotal | freyaCurrency: doc.preview.context.meta.currency }}</b>
                    </div>
                    <div
                      *ngFor="let event of control.events; trackBy: trackById"
                      class="choice-card"
                      [class.selected]="event.selected"
                      [class.disabled]="event.disabled"
                      [style.borderLeftColor]="event.color"
                      [pTooltip]="isLastSelectedEvent(control, event) ? 'At least one event must be included' : undefined"
                      (click)="!event.disabled && !isLastSelectedEvent(control, event) && !vm.state.submitting && toggleEvent(control, event)"
                    >
                      <p-checkbox
                        [binary]="true"
                        [ngModel]="event.selected"
                        [disabled]="event.disabled || isLastSelectedEvent(control, event) || !!vm.state.submitting"
                        (click)="$event.stopPropagation()"
                        (onChange)="toggleEvent(control, event)"
                      ></p-checkbox>
                      <div class="choice-body">
                        <div class="choice-title">{{ event.title }}</div>
                        <small>{{ event.typeName }}<ng-container *ngIf="event.date"> · {{ event.date }}</ng-container></small>
                        <small *ngIf="event.note" class="note">{{ event.note }}</small>
                      </div>
                      <div class="choice-amount">{{ event.total | freyaCurrency: doc.preview.context.meta.currency }}</div>
                    </div>
                    <small *ngIf="control.option.description" class="hint">{{ control.option.description }}</small>
                  </ng-container>
                </ng-container>

                <ng-container *ngSwitchCase="'transactions'">
                  <ng-container *ngIf="control.kind === 'transactions'">
                    <label>{{ control.option.label }}</label>
                    <div *ngIf="!control.transactions.length" class="hint">This job has no transactions.</div>
                    <div
                      *ngFor="let transaction of control.transactions; trackBy: trackById"
                      class="choice-card"
                      [class.selected]="transaction.selected"
                      (click)="!vm.state.submitting && toggleTransaction(control, transaction.id)"
                    >
                      <p-checkbox
                        [binary]="true"
                        [ngModel]="transaction.selected"
                        [disabled]="!!vm.state.submitting"
                        (click)="$event.stopPropagation()"
                        (onChange)="toggleTransaction(control, transaction.id)"
                      ></p-checkbox>
                      <div class="choice-body">
                        <div class="choice-title">{{ transaction.description || 'Transaction' }}</div>
                        <small *ngIf="transaction.date">{{ transaction.date }}</small>
                        <small *ngIf="transaction.receiptSent" class="note">A receipt was already created for this transaction</small>
                      </div>
                      <div class="choice-amount">{{ transaction.amount | freyaCurrency: transaction.currency }}</div>
                    </div>
                    <small *ngIf="control.option.description" class="hint">{{ control.option.description }}</small>
                  </ng-container>
                </ng-container>

                <ng-container *ngSwitchCase="'invoice'">
                  <ng-container *ngIf="control.kind === 'invoice'">
                    <label>{{ control.option.label }}</label>
                    <p-dropdown
                      appendTo="body"
                      placeholder="Select an invoice"
                      [options]="control.invoices"
                      [showClear]="!control.option.required"
                      [ngModel]="control.value"
                      [disabled]="!!vm.state.submitting"
                      (ngModelChange)="setOption(control.option.name, $event ?? undefined)"
                    ></p-dropdown>
                  </ng-container>
                </ng-container>

                <ng-container *ngSwitchCase="'boolean'">
                  <div class="switch-row">
                    <label>{{ control.option.label }}</label>
                    <p-inputSwitch
                      [ngModel]="control.value === true"
                      [disabled]="!!vm.state.submitting"
                      (ngModelChange)="setOption(control.option.name, $event)"
                    ></p-inputSwitch>
                  </div>
                  <small *ngIf="control.option.description" class="hint">{{ control.option.description }}</small>
                </ng-container>

                <ng-container *ngSwitchCase="'text'">
                  <label>{{ control.option.label }}</label>
                  <textarea
                    *ngIf="control.option.kind === 'text' && control.option.multiline; else singleLine"
                    pInputTextarea
                    rows="3"
                    [autoResize]="true"
                    [ngModel]="textValue(control.value)"
                    [disabled]="!!vm.state.submitting"
                    (ngModelChange)="setOption(control.option.name, $event || undefined)"
                  ></textarea>
                  <ng-template #singleLine>
                    <input
                      pInputText
                      type="text"
                      [ngModel]="textValue(control.value)"
                      [disabled]="!!vm.state.submitting"
                      (ngModelChange)="setOption(control.option.name, $event || undefined)"
                    />
                  </ng-template>
                  <small *ngIf="control.option.description" class="hint">{{ control.option.description }}</small>
                </ng-container>

                <ng-container *ngSwitchCase="'money'">
                  <label>{{ control.option.label }}</label>
                  <p-inputNumber
                    mode="currency"
                    [currency]="doc.preview.context.meta.currency"
                    [ngModel]="moneyValue(control.value)"
                    [disabled]="!!vm.state.submitting"
                    (ngModelChange)="setMoney(control.option.name, $event)"
                  ></p-inputNumber>
                  <small *ngIf="control.option.description" class="hint">{{ control.option.description }}</small>
                </ng-container>

                <ng-container *ngSwitchCase="'date'">
                  <label>{{ control.option.label }}</label>
                  <p-calendar
                    appendTo="body"
                    [showButtonBar]="!control.option.required"
                    [ngModel]="dateValue(control.value)"
                    [disabled]="!!vm.state.submitting"
                    (ngModelChange)="setDate(control.option.name, $event)"
                  ></p-calendar>
                  <small *ngIf="control.option.description" class="hint">{{ control.option.description }}</small>
                </ng-container>

                <ng-container *ngSwitchCase="'choice'">
                  <label>{{ control.option.label }}</label>
                  <p-dropdown
                    *ngIf="control.option.kind === 'choice'"
                    appendTo="body"
                    optionLabel="label"
                    optionValue="value"
                    [options]="control.option.choices"
                    [showClear]="!control.option.required"
                    [ngModel]="control.value"
                    [disabled]="!!vm.state.submitting"
                    (ngModelChange)="setOption(control.option.name, $event ?? undefined)"
                  ></p-dropdown>
                  <small *ngIf="control.option.description" class="hint">{{ control.option.description }}</small>
                </ng-container>

              </ng-container>
            </div>

            <div *ngIf="vm.submission === 'send'" class="field">
              <label for="create-document-message">Message to the customer</label>
              <textarea
                id="create-document-message"
                pInputTextarea
                rows="3"
                [placeholder]="'Optional. Added to the email that sends this ' + doc.name.toLowerCase() + '.'"
                [autoResize]="true"
                [ngModel]="doc.message"
                [disabled]="!!vm.state.submitting"
                (ngModelChange)="store.dispatch(actions.messageChanged({ message: $event }))"
              ></textarea>
            </div>

            <p-message
              *ngFor="let option of doc.missing"
              severity="warn"
              [text]="option.label + ' is required'"
            ></p-message>
            <p-message *ngFor="let problem of doc.problems" severity="warn" [text]="problem"></p-message>
            <p-message
              *ngIf="!vm.customerReachable"
              severity="info"
              text="The customer has no email or phone, so documents can only be created and downloaded."
            ></p-message>
          </ng-container>
          <p-message *ngFor="let error of vm.submitErrors" severity="error" [text]="error"></p-message>
        </ng-container>
      </div>

      <div class="preview">
        <ng-container *ngIf="vm.previewed as doc">
          <div *ngIf="vm.documents.length > 1" class="preview-tag">
            <button
              type="button"
              class="preview-step"
              [disabled]="!doc.previous"
              [attr.aria-label]="doc.previous ? 'Previous document: ' + doc.previous.name : 'Previous document'"
              [pTooltip]="doc.previous?.name"
              (click)="doc.previous && store.dispatch(actions.previousDocumentButtonClicked({ docType: doc.previous.docType }))"
            ><i class="pi pi-chevron-left"></i></button>
            <span class="preview-label">Previewing <b>{{ doc.name }}</b> · {{ doc.position }} of {{ vm.documents.length }}</span>
            <button
              type="button"
              class="preview-step"
              [disabled]="!doc.next"
              [attr.aria-label]="doc.next ? 'Next document: ' + doc.next.name : 'Next document'"
              [pTooltip]="doc.next?.name"
              (click)="doc.next && store.dispatch(actions.nextDocumentButtonClicked({ docType: doc.next.docType }))"
            ><i class="pi pi-chevron-right"></i></button>
          </div>
          <app-aegis-document-preview
            *ngIf="doc.preview && !doc.previewLoading"
            [templateSource]="doc.preview.templateSource"
            [context]="doc.context"
            (rendered)="renderError = undefined"
            (renderFailed)="renderError = { preview: doc.preview, message: $event }"
          ></app-aegis-document-preview>
          <div *ngIf="doc.previewLoading" class="preview-status">
            <p-progressSpinner styleClass="spinner" strokeWidth="4"></p-progressSpinner>
            <span>Loading preview…</span>
          </div>
          <div *ngIf="doc.previewError" class="preview-status">
            <p-message severity="error" [text]="doc.previewError"></p-message>
            <button
              pButton
              class="p-button-text"
              icon="pi pi-refresh"
              label="Try again"
              (click)="store.dispatch(actions.retryPreviewButtonClicked({ docType: doc.docType }))"
            ></button>
          </div>
          <p-message *ngIf="renderErrorFor(doc.preview) as error" class="render-error" severity="error" [text]="error"></p-message>
        </ng-container>
        <div *ngIf="!vm.previewed" class="preview-status">
          <span>Select a document to see a preview.</span>
        </div>
      </div>
    </div>

    <ng-template pTemplate="footer">
      <div *ngIf="!vm.state.finished" class="footer">
        <div class="footer-note">
          <ng-container *ngIf="vm.documents.length > 1">
            <button
              *ngIf="vm.needsAttention.length; else oneEmailEach"
              type="button"
              class="attention-link"
              (click)="reviewDocument(vm.needsAttention[0].docType)"
            >
              <i class="pi pi-exclamation-triangle"></i>
              <span *ngIf="vm.needsAttention.length === 1; else several">
                <b>{{ vm.needsAttention[0].name }}</b> needs attention
              </span>
              <ng-template #several><span>{{ vm.needsAttention.length }} documents need attention</span></ng-template>
            </button>
            <ng-template #oneEmailEach>
              <span *ngIf="vm.submission === 'send'">Each document is sent in its own email or SMS.</span>
            </ng-template>
          </ng-container>
        </div>
        <button
          pButton
          class="p-button-text"
          label="Cancel"
          [disabled]="!!vm.state.submitting"
          (click)="close()"
        ></button>
        <ng-container *ngIf="{
          canCreate: !vm.submitBlockedReason && !vm.state.submitting,
          tooltip: vm.state.submitting ? undefined : vm.submitBlockedReason,
          icon: vm.state.submitting ? 'pi pi-spin pi-spinner' : vm.submission === 'send' ? 'pi pi-send' : 'pi pi-file',
          label: vm.state.submitting === 'create' ? 'Creating…'
            : vm.state.submitting === 'send' ? 'Sending…'
            : (vm.submission === 'send' ? 'Send ' : 'Create ') + vm.documentsLabel
        } as button">
          <p-splitButton
            *ngIf="vm.submission === 'send'; else createOnly"
            appendTo="body"
            tooltipPosition="top"
            [pTooltip]="button.tooltip"
            [icon]="button.icon"
            [label]="button.label"
            [model]="submissionMenu(vm.documentsLabel)"
            [buttonDisabled]="!button.canCreate"
            [menuButtonDisabled]="!button.canCreate"
            (onClick)="submit('send')"
          ></p-splitButton>
          <ng-template #createOnly>
            <span tooltipPosition="top" [pTooltip]="button.tooltip">
              <button
                pButton
                [icon]="button.icon"
                [label]="button.label"
                [disabled]="!button.canCreate"
                (click)="submit('create')"
              ></button>
            </span>
          </ng-template>
        </ng-container>
      </div>
    </ng-template>
  </p-dialog>
</ng-container>
